import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import "../styles/Register.css";

export default function Register() {
  const { register, googleLogin, user } = useAuth();
  const nav = useNavigate();

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    mobile: "",
    password: "",
    confirm: "",
  });

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Auto-navigate when user is authenticated (handles both email & Google)
  useEffect(() => {
    if (user) {
      nav("/profile", { replace: true });
    }
  }, [user, nav]);

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const validateForm = () => {
    if (!form.fullName.trim()) return "Full name is required";
    if (!/\S+@\S+\.\S+/.test(form.email)) return "Enter a valid email address";
    if (!/^[0-9]{10}$/.test(form.mobile))
      return "Enter a valid 10-digit mobile number";
    if (form.password.length < 6)
      return "Password must be at least 6 characters";
    if (form.password !== form.confirm) return "Passwords do not match";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      await register(form.email, form.password);
    } catch (err) {
      console.error("Registration error", err);
      setError(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    setLoading(true);
    setError(null);
    try {
      await googleLogin();
    } catch (err) {
      console.error("Google register error:", err);
      setError("Google sign-up failed");
    } finally {
      setLoading(false);
    }
  };

  // Show loading if user state is updating
  if (user === null && loading) {
    return (
      <section className="register-page">
        <motion.div
          className="register-card"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="loading-text">Creating account...</div>
        </motion.div>
      </section>
    );
  }

  return (
    <section className="register-page">
      <motion.div
        className="register-card"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h2 className="register-title">Create Account</h2>
        <p className="register-subtitle">Sign up to get started</p>

        <form className="register-form" onSubmit={submit} noValidate>
          <label htmlFor="fullName">Full Name</label>
          <input
            id="fullName"
            name="fullName"
            placeholder="Full Name"
            value={form.fullName}
            onChange={onChange}
            required
          />

          <label htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            placeholder="you@example.com"
            type="email"
            value={form.email}
            onChange={onChange}
            autoComplete="email"
            required
          />

          <label htmlFor="mobile">Mobile Number</label>
          <input
            id="mobile"
            name="mobile"
            placeholder="10-digit mobile number"
            type="tel"
            value={form.mobile}
            onChange={onChange}
            required
          />

          <label htmlFor="password">Password</label>
          <div className="password-wrapper">
            <input
              id="password"
              name="password"
              placeholder="Password"
              type={showPass ? "text" : "password"}
              value={form.password}
              onChange={onChange}
              autoComplete="new-password"
              required
            />
            <span
              className="password-toggle"
              onClick={() => setShowPass(!showPass)}
              role="button"
              aria-label="Toggle password visibility"
            >
              {showPass ? "🙈" : "👁️"}
            </span>
          </div>

          <label htmlFor="confirm">Confirm Password</label>
          <div className="password-wrapper">
            <input
              id="confirm"
              name="confirm"
              placeholder="Confirm Password"
              type={showConfirm ? "text" : "password"}
              value={form.confirm}
              onChange={onChange}
              autoComplete="new-password"
              required
            />
            <span
              className="password-toggle"
              onClick={() => setShowConfirm(!showConfirm)}
              role="button"
              aria-label="Toggle confirm password visibility"
            >
              {showConfirm ? "🙈" : "👁️"}
            </span>
          </div>

          {error && <div className="error-text">{error}</div>}

          <button className="register-btn" type="submit" disabled={loading}>
            {loading ? "Creating..." : "Create Account"}
          </button>
        </form>

        <div className="divider"><span>or</span></div>

        <button
          className="google-btn"
          onClick={handleGoogleRegister}
          disabled={loading}
        >
          <img
            src="https://www.svgrepo.com/show/475656/google-color.svg"
            alt="Google"
            className="google-icon"
          />
          Sign up with Google
        </button>

        <div className="register-links">
          <p>
            Already have an account?{" "}
            <Link to="/login" className="link">Login here</Link>
          </p>
        </div>
      </motion.div>
    </section>
  );
}
